import { styled, Box } from '@mui/material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { MuiCardForm } from 'lib/form/MuiCardForm';
import { SettingDisplay } from 'lib/SettingDisplay';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { ImageFormField } from 'lib/form/fields/ImageFormField';
import { Option } from 'lib/types';
import { SettingDisplaySwitch } from '../SettingDisplaySwitch';
import { KeyFileInputFormField } from 'lib/form/fields/KeyFileInputFormField';
import { SettingDisplayText } from '../SettingDisplayText';
import { SettingDisplayColor } from '../SettingDisplayColor';
import { SettingDisplayChip, SettingDisplayChips } from '../SettingsDisplayChip';
import { getSelectedOption, getSelectedOptions } from 'components/utils/getSelectedOptions';
import { ColorFormField, RGBAValue } from 'lib/form/fields/ColorFormField';
import { isString } from 'lib/typeguards';
import { MultiSelectFormField } from 'lib/form/fields/MultiSelectFormField';
import { SelectFormField } from 'lib/form/fields/SelectFormField';

const PREFIX = 'MarketingSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`,
    image: `${PREFIX}-image`,
    googleWalletHero: `${PREFIX}-googleWalletHero`,
    appleWalletLogo: `${PREFIX}-appleWalletLogo`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2),
        overflowY: 'visible' // Needed for the dropdowns
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    },
    [`& .${classes.image}`]: {
        width: 'auto',
        height: 'auto',
        maxHeight: '400px',
        maxWidth: '100%'
    },
    [`& .${classes.googleWalletHero}`]: {
        width: '300px',
        height: 'auto'
    },
    [`& .${classes.appleWalletLogo}`]: {
        maxWidth: '400px',
        height: 'auto'
    }
}));

const rgbToString = (value: RGBAValue | string) => {
    if (isString(value)) {
        return value;
    }
    return `rgb(${value.r}, ${value.g}, ${value.b})`;
};

const walletColumnOptions: Option[] = [
    { label: 'Number of available awards', value: 'awards.length' },
    { label: 'Number of stamps for a specific perk', value: 'award.stamps' },
    { label: 'Number of points for a specific perk', value: 'award.points' },
    { label: 'Title of a specific perk', value: 'perk.title' },
    { label: "Customer's full name", value: 'user.name' }
];

const walletDetailOptions: Option[] = [
    { label: "Customer's last transaction", value: 'user.lastTransaction' },
    { label: 'List of available awards', value: 'perks.title' },
    { label: 'Title of the perk', value: 'perk.title' },
    { label: 'Description of the perk', value: 'perk.description' },
    { label: 'Terms and conditions of the perk', value: 'perk.terms' },
    { label: 'Web link of the perk', value: 'perk.link' }
];

type IProps = {
    settings: IPublicAndPrivateSettings;
    tenantId: string;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export function LoyaltyCardInWalletSettings({ settings, tenantId, onSubmit }: IProps) {
    // TODO: migrate the banner image to new folder location
    const appleWalletImage = `${
        import.meta.env.MEDIA_SERVICE_URL
    }/tenants/${tenantId}/apple_wallet/strip.png`;
    const googleWalletHero = `${
        import.meta.env.MEDIA_SERVICE_URL
    }/tenants/${tenantId}/app_assets/google_wallet/hero`;
    return (
        <StyledMuiCardForm
            title="Loyalty Card in Wallet"
            initialValues={{
                appleWalletEnabled: settings?.appleWalletEnabled ?? false,
                appleWalletDescription: settings?.appleWallet?.description,
                appleWalletLogoText: settings?.appleWallet?.logoText,
                appleWalletBackgroundColor: settings?.appleWallet?.backgroundColor,
                appleWalletForegroundColor: settings?.appleWallet?.foregroundColor,
                appleWalletImage,
                appleWalletLogo: settings?.appleWallet?.logoImage,
                appleWalletP12BundleAsBase64: settings?.appleWallet?.p12BundleAsBase64,
                appleWalletPassTypeIdentifier: settings?.appleWallet?.passTypeIdentifier,
                googleWalletEnabled: settings?.googleWalletEnabled ?? false,
                googleWalletIssuerId: settings?.googleWallet?.issuerId,
                googleWalletClassId: settings?.googleWallet?.classId,
                googleWalletClientEmail: settings?.googleWallet?.clientEmail,
                googleWalletHero,
                walletColumn1: settings?.wallet?.column1,
                walletColumn2: settings?.wallet?.column2,
                walletDetail: getSelectedOptions(walletDetailOptions, settings?.wallet?.detail ?? [])
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplaySwitch
                        title="Apple Wallet Enabled"
                        checked={settings?.appleWalletEnabled}
                    />
                    <SettingDisplayText
                        title="Wallet Page Header"
                        text={settings?.appleWallet?.description}
                    />
                    <SettingDisplayText title="Wallet Card Title" text={settings?.appleWallet?.logoText} />
                    <SettingDisplay
                        label="Logo Image"
                        description="This is optional. It will default to App Icon if missing. 480px by 150px PNG or JPEG"
                    >
                        <img src={settings?.appleWallet?.logoImage} className={classes.image} />
                    </SettingDisplay>
                    <SettingDisplayColor
                        title="Background Colour"
                        color={settings?.appleWallet?.backgroundColor}
                    />
                    <SettingDisplayColor title="Text Colour" color={settings?.appleWallet?.foregroundColor} />
                    <SettingDisplay
                        label="Image"
                        description="Recommend image dimension 350px by 160px PNG or JPEG"
                    >
                        <img src={appleWalletImage} className={classes.image} />
                    </SettingDisplay>
                    <SettingDisplayText
                        title="P12 Bundle File"
                        text={settings?.appleWallet?.p12BundleAsBase64}
                        crop
                    />
                    <SettingDisplayText
                        title="Pass Type Identifier"
                        text={settings?.appleWallet?.passTypeIdentifier}
                    />
                    <SettingDisplaySwitch
                        title="Google Wallet Enabled"
                        checked={settings?.googleWalletEnabled}
                    />
                    <SettingDisplayText title="Issuer ID" text={settings?.googleWallet?.issuerId} />
                    <SettingDisplayText title="Class ID" text={settings?.googleWallet?.classId} />
                    <SettingDisplayText title="Client Email" text={settings?.googleWallet?.clientEmail} />
                    <SettingDisplay
                        label="Hero Image"
                        description="Recommend image dimension 1032px by 336px PNG or JPEG. This is optional."
                    >
                        <img src={googleWalletHero} className={classes.image} />
                    </SettingDisplay>
                    <SettingDisplayChip
                        title="Pass Information - Column 1"
                        value={getSelectedOption(walletColumnOptions, settings?.wallet?.column1)}
                    />
                    <SettingDisplayChip
                        title="Pass Information - Column 2"
                        value={getSelectedOption(walletColumnOptions, settings?.wallet?.column2)}
                    />
                    <SettingDisplayChips
                        title="Wallet Details"
                        value={getSelectedOptions(walletDetailOptions, settings?.wallet?.detail)}
                    />
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Apple Wallet Enabled">
                        <SwitchFormField name="appleWalletEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="Wallet Page Header">
                        <TextFormField
                            name="appleWalletDescription"
                            description="The title of the screen when adding the pass to Apple wallet"
                        />
                    </SettingDisplay>
                    <SettingDisplay label="Wallet Card Title">
                        <TextFormField
                            name="appleWalletLogoText"
                            description="The title displayed next to icon on wallet pass"
                        />
                    </SettingDisplay>
                    <SettingDisplay
                        label="Logo Image"
                        description="This is optional. It will default to App Icon if missing. 480px by 150px PNG or JPEG"
                    >
                        <Box className={classes.appleWalletLogo}>
                            <ImageFormField
                                name="appleWalletLogo"
                                aspect={480 / 150}
                                fileName="logo"
                                uploadFolder={encodeURIComponent('app_assets/apple_wallet')}
                            />
                        </Box>
                    </SettingDisplay>
                    <SettingDisplay label="Background Colour">
                        <ColorFormField name="appleWalletBackgroundColor" valueFormatter={rgbToString} />
                    </SettingDisplay>
                    <SettingDisplay label="Text Colour">
                        <ColorFormField name="appleWalletForegroundColor" valueFormatter={rgbToString} />
                    </SettingDisplay>
                    <SettingDisplay
                        label="Image"
                        description="Recommend image dimension 350px by 160px PNG or JPEG"
                    >
                        <ImageFormField
                            name="appleWalletImage"
                            aspect={350 / 160}
                            fileName="strip"
                            uploadFolder="apple_wallet"
                            unremovable
                        />
                    </SettingDisplay>
                    <SettingDisplay label="P12 Bundle File">
                        <KeyFileInputFormField
                            isText={false}
                            name="appleWalletP12BundleAsBase64"
                            attributes={{ type: 'file', accept: '.p12' }}
                        />
                    </SettingDisplay>
                    <SettingDisplay label="Pass Type Identifier">
                        <TextFormField name="appleWalletPassTypeIdentifier" />
                    </SettingDisplay>
                    <SettingDisplay label="Google Wallet Enabled">
                        <SwitchFormField name="googleWalletEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="Issuer ID">
                        <TextFormField name="googleWalletIssuerId" />
                    </SettingDisplay>
                    <SettingDisplay label="Class ID">
                        <TextFormField name="googleWalletClassId" />
                    </SettingDisplay>
                    <SettingDisplay label="Client Email">
                        <TextFormField name="googleWalletClientEmail" />
                    </SettingDisplay>
                    <SettingDisplay
                        label="Hero Image"
                        description="Recommend image dimension 1032px by 336px PNG or JPEG. This is optional."
                    >
                        <Box className={classes.googleWalletHero}>
                            <ImageFormField
                                name="googleWalletHero"
                                aspect={258 / 84}
                                fileName="hero"
                                uploadFolder={encodeURIComponent('app_assets/google_wallet')}
                                unremovable
                            />
                        </Box>
                    </SettingDisplay>
                    <SettingDisplay label="Pass Information - Column 1">
                        <SelectFormField
                            name="walletColumn1"
                            options={walletColumnOptions}
                            clearable
                            displayEmpty
                        />
                    </SettingDisplay>
                    <SettingDisplay label="Pass Information - Column 2">
                        <SelectFormField
                            name="walletColumn2"
                            options={walletColumnOptions}
                            clearable
                            displayEmpty
                        />
                    </SettingDisplay>
                    <SettingDisplay label="Wallet Details">
                        <Box flexGrow={1}>
                            <MultiSelectFormField name="walletDetail" options={walletDetailOptions} isMulti />
                        </Box>
                    </SettingDisplay>
                </Box>
            }
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
}
